import axios from 'axios';
import apiClient from './apiClient';

export type Industry = { id: string; name: string; slug: string };

export type ComparisonLocation = {
  id: string;
  name: string;
  country_code: string;
  region_code?: string;
  type: 'country' | 'state' | 'city';
  group?: string;
};

export type LeadSourceApiRow = {
  id: number;
  name: string;
  url: string;
  logo_url?: string | null;
  pricing_model: string;
  cost_per_lead?: number | null;
  monthly_cost_min?: number | null;
  monthly_cost_max?: number | null;
  quality_score: number;
  response_rate_pct?: number | null;
  features?: Record<string, unknown> | null;
  best_for?: Record<string, unknown> | null;
  description?: string | null;
  final_score: number;
};

export type ComparisonScoreResponse = {
  sources: LeadSourceApiRow[];
  total_sources: number;
  query_id?: number;
};

export type EmailSignupResponse = {
  success: boolean;
  message: string;
  query_id: number;
  email: string;
};

function extractErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }
  const data = error.response?.data;
  if (!data || typeof data !== 'object' || data === null || !('detail' in data)) {
    return fallback;
  }
  const detail = (data as { detail: unknown }).detail;
  return typeof detail === 'string' ? detail : fallback;
}

export async function getIndustries(): Promise<Industry[]> {
  try {
    const response = await apiClient.get<{ industries: Industry[] }>('/comparison/industries');
    return response.data.industries ?? [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to load options. Please refresh.'));
  }
}

export async function getLocations(): Promise<ComparisonLocation[]> {
  try {
    const response = await apiClient.get<{ locations: ComparisonLocation[] }>(
      '/comparison/locations'
    );
    return response.data.locations ?? [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to load options. Please refresh.'));
  }
}

export async function scoreSources(
  industry: string,
  location: string,
  locationId?: string | null,
  targetMarket?: string | null,
  emailPriority?: string | null,
  startingPoint?: string | null,
): Promise<ComparisonScoreResponse> {
  try {
    const response = await apiClient.post<ComparisonScoreResponse>('/comparison/score', {
      industry,
      location,
      location_id: locationId ?? null,
      target_market: targetMarket ?? null,
      email_priority: emailPriority ?? null,
      starting_point: startingPoint ?? null,
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to get results. Please try again.'));
  }
}

export async function emailSignup(
  emailAddress: string,
  industry?: string | null,
  location?: string | null,
  _unused?: string | null,
  startingPoint?: string | null,
  queryId?: number
): Promise<EmailSignupResponse> {
  try {
    const response = await apiClient.post<EmailSignupResponse>('/comparison/email-signup', {
      email: emailAddress,
      industry: industry ?? null,
      location: location ?? null,
      starting_point: startingPoint ?? null,
      query_id: queryId ?? undefined,
    });
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Failed to sign up. Please try again.'));
  }
}
